import { Injectable, inject } from '@angular/core';
import { Borders, Workbook, Worksheet } from 'exceljs';
import jsPDF from 'jspdf';
import autoTable, { Styles } from 'jspdf-autotable';
import { reportConstants, reportConstantsMessage } from '../../common/constants';
import { SwellingFinalReport, Vw_SwellCalculatedCompositionalBalanceData, Vw_SwellInjectionGasCompositionData, Vw_SwellReportData } from '../../swell/models/swelling';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { setNumericOrTextCellValue } from './report.cellFormat.util';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFSwellingRenderService {
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);

    private commonStylesWithoutBorder = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0
    };

    private commonHeadStylesWithoutBorder = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedBold,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    private commonBodyStylesWithoutBorder = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    SwellingReport(doc: jsPDF, marginSize: number, swellingExp: SwellingFinalReport, wellName: string, appendixText = ''): void {
        if (swellingExp !== undefined && swellingExp !== null) {
            marginSize += 10;
            const val = swellingExp;
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedBold);
            let currentY = 30;

            doc.text(appendixText + wellName + ' ' + reportConstants.summaryOfSwellingTestSepGas, marginSize - 5, currentY);
            currentY += 10;
            doc.setFont(reportConstants.eMprintCondensedRegular);

            const groupedByTemp = new Map<string | number, Vw_SwellReportData[]>();
            for (const item of val.swellReport) {
                const temp = item.temperature ?? '-';
                if (!groupedByTemp.has(temp)) groupedByTemp.set(temp, []);
                groupedByTemp.get(temp)!.push(item);
            }

            let isLastRowDrawn = false;
            const columnDecimals = reportConstants.swellReportColumnDecimals;

            for (const [temp, rows] of groupedByTemp.entries()) {
                doc.setFont(reportConstants.eMprintCondensedRegular);
                const isNumericTemp = typeof temp === reportConstants.number || (typeof temp === reportConstants.string && !isNaN(Number(temp)));
                const tempLabel = isNumericTemp ? `${temp}°F` : `${temp}`;
                doc.text(`${reportConstants.temperature}: ${tempLabel}`, marginSize, currentY);
                currentY += 5;

                autoTable(doc, {
                    head: [
                        [
                            { content: reportConstants.headSwelling, styles: { valign: reportConstants.middle, halign: reportConstants.center } },
                            { content: reportConstants.headSwelling1, styles: { valign: reportConstants.middle, halign: reportConstants.center } },
                            { content: reportConstants.headSwelling2, styles: { valign: reportConstants.middle, halign: reportConstants.center } },
                            { content: reportConstants.headSwelling3, styles: { valign: reportConstants.middle, halign: reportConstants.center } },
                            { content: reportConstants.headSwelling4, styles: { valign: reportConstants.middle, halign: reportConstants.center } },
                            { content: reportConstants.headSwelling5, styles: { valign: reportConstants.middle, halign: reportConstants.center } },
                            { content: reportConstants.headSwelling6, styles: { valign: reportConstants.middle, halign: reportConstants.center } }
                        ],
                        reportConstants.headSwelling7,
                        reportConstants.headSwelling8
                    ],
                    body: rows.map((r: Vw_SwellReportData) => [
                        r.stage ?? '', r.moleFracInjGas ?? '', r.saturationPressure ?? '', r.systemType ?? '',
                        r.relativeVolume ?? '', r.densityAtSatPressure ?? '', r.differenceGOR ?? ''
                    ]),
                    startY: Math.round(currentY),
                    theme: reportConstants.plain,
                    margin: { left: marginSize - 5, right: marginSize - 5, top: 40 },
                    styles: this.commonStylesWithoutBorder as Partial<Styles>,
                    headStyles: {
                        fontSize: 12,
                        halign: reportConstants.center,
                        textColor: [0, 0, 0],
                        fillColor: false,
                        fontStyle: reportConstants.normal,
                        font: reportConstants.eMprintCondensedBold,
                        cellPadding: { top: 0.5, right: 1, bottom: 0.5, left: 1 },
                        lineWidth: 0,
                        valign: reportConstants.middle
                    },
                    columnStyles: {
                        0: { cellWidth: 15 },
                        1: { cellWidth: 25 },
                        2: { cellWidth: 25 },
                        3: { cellWidth: 20 },
                        4: { cellWidth: 20 },
                        5: { cellWidth: 20 },
                        6: { cellWidth: 25 }
                    },
                    bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                    didParseCell: function (data) {
                        const decimals = columnDecimals[data.column.index];
                        if (data.section === reportConstants.body && decimals !== undefined && typeof data.cell.raw === 'number') {
                            data.cell.text = [data.cell.raw.toFixed(decimals)];
                        }
                    },
                    didDrawCell: function (data) {
                        const { cell, row, doc, section } = data;
                        if (section === reportConstants.head) {
                            const x = cell.x;
                            const y = cell.y;
                            const w = cell.width;
                            const h = cell.height;
                            doc.setDrawColor(0, 0, 0);
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            if (row.index === 0) {
                                doc.line(x, y, x + w, y);
                                doc.line(x, y, x, y + h);
                                doc.line(x + w, y, x + w, y + h);
                            } else if (row.index === 2) {
                                doc.line(x, y + h, x + w, y + h);
                                doc.line(x, y, x, y + h);
                                doc.line(x + w, y, x + w, y + h);
                            } else {
                                doc.line(x, y, x, y + h);
                                doc.line(x + w, y, x + w, y + h);
                            }
                        } else {
                            doc.setDrawColor(0, 0, 0);
                            doc.setLineWidth(reportConstants.tableLinewidth);
                            doc.rect(cell.x, cell.y, cell.width, cell.height);
                        }
                        const rowCount = rows.length;
                        if (data.row.index === rowCount - 1 && data.section === reportConstants.body) {
                            isLastRowDrawn = true;
                        }
                    }
                });

                if (doc.lastAutoTable) {
                    currentY = doc.lastAutoTable.finalY + 10;
                }

                if (currentY > doc.internal.pageSize.getHeight() - 60) {
                    doc.addPage();
                    currentY = 30;
                }
            }

            if (isLastRowDrawn) {
                const marginSizeBox = 20;
                const boxX = marginSizeBox;
                const lineHeight = 5;
                const boxHeight = lineHeight * 5 + 6;
                const pageHeight = doc.internal.pageSize.getHeight();
                const boxY = pageHeight - boxHeight - marginSizeBox;

                doc.setFontSize(10);
                doc.setFont(reportConstants.eMprintCondensedRegular);
                const textY = boxY + lineHeight;
                doc.text(reportConstants.commentsSwelling[0], boxX + 2, textY + lineHeight);
                doc.text(reportConstants.commentsSwelling[1], boxX + 2, textY + lineHeight * 2);
                doc.text(reportConstants.commentsSwelling[2], boxX + 2, textY + lineHeight * 3);
            }

            if (val.swellReportGraphData.length > 0) {
                doc.addPage();
                doc.setFont(reportConstants.eMprintCondensedBold);
                let graphY = 30;
                doc.text(wellName + ' ' + reportConstants.summaryOfSwellingTestSepGas, marginSize, graphY);
                graphY += 5;
                doc.setFont(reportConstants.eMprintCondensedRegular);

                const pageWidth = doc.internal.pageSize.getWidth();
                const imageWidth = pageWidth * 0.8;
                const xPos = (pageWidth - imageWidth) / 2;

                const pdfW = 150;
                const pdfH = 115;

                doc.addImage(
                    val.swellReportGraphData[0],
                    reportConstants.jpeg,
                    xPos + 7,
                    graphY,
                    pdfW,
                    pdfH,
                    undefined,
                    'FAST'
                );

            }
        }
    }

    SwellingGasCompDataReport(doc: jsPDF, marginSize: number, swellingExp: SwellingFinalReport, wellName: string): void {
        if (swellingExp !== undefined && swellingExp !== null) {
            marginSize += 15;
            const val = swellingExp;
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedBold);
            let currentY = 30;

            doc.text(wellName + ' ' + reportConstants.summaryOfSwellingTestSepGas, marginSize, currentY);
            currentY += 5;
            doc.setFont(reportConstants.eMprintCondensedRegular);
            currentY += 5;

            autoTable(doc, {
                head: [
                    [
                        {
                            content: reportConstants.component, colSpan: 2, styles: { valign: reportConstants.middle, halign: reportConstants.center }
                        },
                        {
                            content: reportConstants.weight, styles: { valign: reportConstants.middle, halign: reportConstants.center }
                        },
                        {
                            content: reportConstants.molePercent, styles: { valign: reportConstants.middle, halign: reportConstants.center }
                        }
                    ]
                ],
                body: val.swellInjectionGasComposition.map((val: Vw_SwellInjectionGasCompositionData) => [val.component ?? '', val.componentName ?? '', val.weightPercent ?? '', val.molePercent ?? '']),
                startY: Math.round(currentY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize, top: 40 },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                didDrawPage: function () {
                    let pageY = 30;
                    doc.setFontSize(12);
                    doc.setFont(reportConstants.eMprintCondensedBold);
                    pageY += 5;
                    doc.setFont(reportConstants.eMprintCondensedRegular);
                    doc.text(reportConstants.injectionGasComposition, marginSize, pageY);
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && (data.column.index === 0 || data.column.index === 1)) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                    if (data.section === reportConstants.body && (data.column.index === 2 || data.column.index === 3) && typeof data.cell.raw === 'number') {
                        data.cell.text = [data.cell.raw.toFixed(3)];
                    }
                    if (data.section === reportConstants.head) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, doc, section } = data;
                    if (section === reportConstants.head) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        if (row.index === 0) {
                            doc.line(x, y, x + w, y);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else if (row.index === 2) {
                            doc.line(x, y + h, x + w, y + h);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else {
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        }
                    } else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }
                }
            });
        }
    }

    SwellingCalculatedCompBalData(doc: jsPDF, marginSize: number, swellingExp: SwellingFinalReport, wellName: string): void {
        if (swellingExp !== undefined && swellingExp !== null) {
            marginSize += 15;
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedBold);
            let currentY = 30;

            doc.text(wellName + ' ' + reportConstants.summaryOfSwellingTestSepGas, marginSize, currentY);
            currentY += 5;
            doc.setFont(reportConstants.eMprintCondensedRegular);
            currentY += 5;

            const additionsCount = 3;
            const firstHeader = [
                { content: '', colSpan: 2, styles: { halign: reportConstants.center, valign: reportConstants.middle } },
                { content: reportConstants.originalFluid, styles: { halign: reportConstants.center } },
                ...Array.from({ length: additionsCount }, (_, i) => ({
                    content: `Addition ${i + 1}`,
                    styles: { halign: reportConstants.center }
                }))
            ];
            const secondHeader = [
                { content: '', colSpan: 2 },
                { content: '', styles: { halign: reportConstants.center } },
                ...Array.from({ length: additionsCount }, (_, i) => {
                    const key = `${reportConstants.addition}${i + 1}` as keyof Vw_SwellCalculatedCompositionalBalanceData;
                    const rawVal = swellingExp.swellCalculatedCompositionalBalance[0][key];
                    const displayVal = (rawVal !== null && rawVal !== undefined && rawVal !== '')
                        ? +(Number(rawVal) * 100).toFixed(2)
                        : '';
                    return {
                        content: `${displayVal}${reportConstants.percentage}`,
                        styles: { halign: reportConstants.center }
                    };
                })
            ];
            const thirdHeader = [
                { content: reportConstants.component, colSpan: 2, styles: { halign: reportConstants.center } },
                { content: reportConstants.molePercent, styles: { halign: reportConstants.center } },
                ...Array.from({ length: additionsCount }, () => ({
                    content: reportConstants.molePercent,
                    styles: { halign: reportConstants.center }
                }))
            ];

            const headRows = [firstHeader, secondHeader, thirdHeader];

            const items = swellingExp.swellCalculatedCompositionalBalance;

            const bodyRows = items
                .filter((item: Vw_SwellCalculatedCompositionalBalanceData) =>
                    item.component !== reportConstants.totals &&
                    item.originalFluidMolePercent !== null &&
                    item.originalFluidMolePercent !== undefined &&
                    Number(item.originalFluidMolePercent) !== 0
                )
                .map((item: Vw_SwellCalculatedCompositionalBalanceData) => {
                    const row: (string | number)[] = [
                        item.component,
                        item.componentName,
                        item.originalFluidMolePercent ?? ''
                    ];
                    for (let i = 1; i <= additionsCount; i++) {
                        row.push(item[`${reportConstants.addition}${i}${reportConstants.molPct}`] ?? '');
                    }
                    return row;
                });

            const totals = items.find((it: Vw_SwellCalculatedCompositionalBalanceData) => it.component === reportConstants.totals);
            if (totals) {
                const totalsRow: (string | number)[] = [
                    reportConstants.totals,
                    '',
                    totals.sumMolPct ?? totals.originalFluidMolePercent ?? ''
                ];
                for (let i = 1; i <= additionsCount; i++) {
                    const sumKey = `${reportConstants.sumAdd}${i}${reportConstants.molPct}` as `sumAdd${number}MolPct`;
                    const addKey = `${reportConstants.addition}${i}${reportConstants.molPct}` as `addition${number}MolPct`;
                    totalsRow.push(totals[sumKey] ?? totals[addKey] ?? '');
                }
                bodyRows.push(totalsRow);
            }

            autoTable(doc, {
                head: headRows,
                body: bodyRows,
                startY: Math.round(currentY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize, top: 40 },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                didDrawPage: function () {
                    doc.setFontSize(12);
                    doc.setFont(reportConstants.eMprintCondensedBold);
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && (data.column.index === 0 || data.column.index === 1)) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                    if (data.section === reportConstants.body && data.column.index >= 2 && typeof data.cell.raw === 'number') {
                        data.cell.text = [data.cell.raw.toFixed(3)];
                    }
                    if (data.section === reportConstants.head) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, column, doc, section } = data;
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;

                        doc.setDrawColor(255, 255, 255);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                    else if (section === reportConstants.head && row.index === 1 && column.index === 0) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;

                        doc.setDrawColor(255, 255, 255);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                    else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }

                    if (section === reportConstants.head && row.index === 2) {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.line(x, y + h - 0.5, x + w, y + h - 0.5);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }

                }
            });
        }
    }

    GenerateSwellingReport(workbook: Workbook, worksheetName: string, swellingExp: SwellingFinalReport, wellName: string, subHeader: string, fromCall = '', letter = '') {
        try {
            const swellingWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            swellingWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(swellingWorksheet, undefined, {
                paperSize: undefined,
                orientation: reportConstants.landscape, //we can set it to 'portrait' or 'landscape'
                fitToPage: false,
                fitToWidth: 0,//page 1
                fitToHeight: 0, // 1 page , 0 automatic
                scale: 90,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });
            DisplayLogoAndWellName(this, swellingWorksheet);

            if (swellingExp !== undefined && swellingExp !== null) {
                const val = swellingExp;
                let rowNo = 7;

                const groupedByTemp = new Map<string | number, Vw_SwellReportData[]>();
                for (const item of val.swellReport) {
                    const temp = item.temperature ?? '-';
                    if (!groupedByTemp.has(temp)) {
                        groupedByTemp.set(temp, []);
                    }
                    groupedByTemp.get(temp)!.push(item);
                }

                swellingWorksheet.mergeCells(reportConstants.b4, reportConstants.h5);
                const textReportTitleCell = swellingWorksheet.getCell(reportConstants.b4);
                let subHeadingValue="";
                if (fromCall === reportConstants.appendix) {
                    subHeadingValue = reportConstants.appendix + ' ' + letter + ': ' + wellName + ' ' + reportConstants.swellInjectionAppendix;
                }
                else {
                    subHeadingValue = wellName + ' ' + reportConstants.swellInjection;
                }
                textReportTitleCell.value =subHeadingValue;
                textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };

                for (const [temp, rows] of groupedByTemp.entries()) {
                    const tempCell = swellingWorksheet.getCell(reportConstants.b + rowNo);
                    const isNumericTemp = typeof temp === reportConstants.number || (typeof temp === 'string' && !isNaN(Number(temp)));
                    const tempLabel = isNumericTemp ? `${temp}°F` : `${temp}`;
                    tempCell.value = `${reportConstants.temperature}: ${tempLabel}`;
                    tempCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
                    rowNo++;

                    const tableHeader = reportConstants.swellInjectionHead;

                    const tableBody = rows.map(r => [
                        r.stage?.toString() ?? '',
                        r.moleFracInjGas?.toFixed(3) ?? '',
                        r.saturationPressure?.toFixed(0) ?? '',
                        r.systemType?.toString() ?? '',
                        r.relativeVolume?.toFixed(3) ?? '',
                        r.densityAtSatPressure?.toFixed(3) ?? '',
                        r.differenceGOR?.toFixed(1) ?? ''
                    ]);

                    const tableStartRow = rowNo;
                    const startCol = 2;
                    GenerateTableWithBorders(swellingWorksheet, tableHeader, tableBody, tableStartRow, startCol, borderStyle);

                    const headerRows = 3;
                    const r1 = tableStartRow;
                    const r2 = r1 + 1;
                    const r3 = r1 + 2;
                    const colCount = 7;

                    const mergedHeaders: string[] = reportConstants.swellInjectionHeadFormatted;

                    mergeThreeRowHeader(
                        swellingWorksheet,
                        r1,
                        startCol,
                        colCount,
                        mergedHeaders,
                        reportConstants.eMprintCondensed
                    );

                    const columnWidths = [12, 14, 16, 12, 16, 16, 20];
                    columnWidths.forEach((width, i) => {
                        swellingWorksheet.getColumn(startCol + i).width = width;
                    });

                    swellingWorksheet.getRow(r1).height = 70;
                    swellingWorksheet.getRow(r2).height = 4;
                    swellingWorksheet.getRow(r3).height = 4;

                    rowNo = tableStartRow + headerRows + tableBody.length + 2;
                }

                AddComment(
                    swellingWorksheet,
                    reportConstants.b,
                    rowNo,
                    reportConstants.commentsSwelling[0]
                );
                rowNo++;
                AddComment(
                    swellingWorksheet,
                    reportConstants.b,
                    rowNo,
                    reportConstants.commentsSwelling[1]
                );
                rowNo++;
                AddComment(
                    swellingWorksheet,
                    reportConstants.b,
                    rowNo,
                    reportConstants.commentsSwelling[2]
                );
                rowNo += 2;

                swellingWorksheet.mergeCells(reportConstants.m4, reportConstants.w5);
                const textReportTitleCellPlot = swellingWorksheet.getCell(reportConstants.m4);
                textReportTitleCellPlot.value = subHeadingValue;
                textReportTitleCellPlot.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };

                DisplayLogoAndWellNamePlot(this, swellingWorksheet);
                const vVsat = workbook.addImage({
                    base64: val.swellReportGraphData[0],
                    extension: reportConstants.png,
                });
                swellingWorksheet.mergeCells(reportConstants.m6, reportConstants.w34);
                swellingWorksheet.addImage(vVsat, {
                    tl: { col: 12, row: 9 },
                    ext: { width: 600, height: 400 }
                });
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function mergeThreeRowHeader(
            ws: Worksheet,
            startRow: number,
            startCol: number,
            colCount: number,
            mergedHeaders: string[],
            fontName = reportConstants.eMprintCondensed
        ) {
            const r1 = startRow;
            const r3 = startRow + 2;

            for (let i = 0; i < colCount; i++) {
                const col = startCol + i;
                ws.mergeCells(r1, col, r3, col);
                const cell = ws.getCell(r1, col);
                cell.value = mergedHeaders[i] ?? '';
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
                cell.font = { name: fontName, size: 12, bold: true };
                cell.border = {
                    top: { style: reportConstants.thin },
                    left: { style: reportConstants.thin },
                    bottom: { style: reportConstants.thin },
                    right: { style: reportConstants.thin }
                };
            }
        }

        function GenerateTableWithBorders(
            worksheet: Worksheet,
            tableHeader: string[][],
            tableBody: (string | number)[][],
            tableStartRow: number,
            tableStartColumn: number,
            borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; } }
        ) {
            tableHeader.forEach((headerRow, idx) => {
                headerRow.forEach((header, colIdx) => {
                    const cell = worksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx);
                    cell.value = header;
                    cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
                    cell.border = {
                        top: idx === 0 ? { style: reportConstants.thin } : borderStyle.top,
                        bottom: idx === tableHeader.length - 1 ? { style: reportConstants.double } : borderStyle.bottom,
                        left: colIdx === 0 ? { style: reportConstants.thin } : borderStyle.left,
                        right: colIdx === headerRow.length - 1 ? { style: reportConstants.thin } : borderStyle.right,
                    } as Partial<Borders>;

                    const headerValue = header.split('\n');
                    if (headerValue && headerValue.length > 0 && headerValue[0].length > 0) {
                        worksheet.getColumn(tableStartColumn + colIdx).width = headerValue[0].length + 2;
                    }
                    else {
                        worksheet.getColumn(tableStartColumn + colIdx).width = 8;
                    }
                    worksheet.getRow(tableStartRow + idx).height = 80;
                });
            });

            tableBody.forEach((row, rowIdx) => {
                row.forEach((cellValue, colIdx) => {
                    const cell = worksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
                    setNumericOrTextCellValue(cell, cellValue);
                    cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                    cell.border = {
                        bottom: rowIdx === tableBody.length - 1 ? { style: reportConstants.thin } : borderStyle.bottom,
                        left: colIdx === 0 ? { style: reportConstants.thin } : borderStyle.left,
                        right: colIdx === row.length - 1 ? { style: reportConstants.thin } : borderStyle.right,
                    } as Partial<Borders>;
                });
            });
        }

        function AddComment(worksheet: Worksheet, column: string, rowNo: number, value: string, fontSize = 11) {
            const textCommentsCell = worksheet.getCell(column + rowNo);
            textCommentsCell.value = value;
            textCommentsCell.font = { name: reportConstants.eMprintCondensed, size: fontSize, bold: false };
            textCommentsCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            worksheet.getRow(rowNo).height = 25;
        }

        async function DisplayLogoAndWellName(report: ReportPDFSwellingRenderService, worksheet: Worksheet) {
            
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png
            });

            
            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart =  reportConstants.c;
                const columnEnd = reportConstants.h;
                report._reportGeneralUtilityService.AddSubHeaderToWorksheet(worksheet, subHeader, columnStart + reportConstants.one, columnEnd + '2');
            }
        }
        async function DisplayLogoAndWellNamePlot(report: ReportPDFSwellingRenderService, worksheet: Worksheet) {
            
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png
            });

            
            worksheet.addImage(exxonLogoId, {
                tl: { col: 12, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart =  reportConstants.p;
                const columnEnd = reportConstants.w;
                // Logo occupies rows 1-2, so the subheader must start at row 3 to avoid overlapping merges
                report._reportGeneralUtilityService.AddSubHeaderToWorksheet(worksheet, subHeader, columnStart + '1', columnEnd + '2');
            }
        }
    }

    GenerateSwellingGasCompDataReport(workbook: Workbook, worksheetName: string, swellingExp: SwellingFinalReport, wellName: string, subHeader: string) {
        try {
            const swellingWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            swellingWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(swellingWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.landscape, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,//page 1
                fitToHeight: 1, // 1 page , 0 automatic
                scale: 95,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            DisplayLogoAndWellName(this, swellingWorksheet);

            if (swellingExp !== undefined && swellingExp !== null) {
                const val = swellingExp;
                let rowNo = 9;
                swellingWorksheet.mergeCells(reportConstants.c9, reportConstants.d9);
                const tableHeader = [
                    ['', reportConstants.component, reportConstants.weight, reportConstants.molePercent, reportConstants.targetMole, reportConstants.errorPercentage]
                ];

                const tableBody = val.swellInjectionGasComposition.map((val: Vw_SwellInjectionGasCompositionData) => [
                    val.component?.toString() ?? '',
                    val.componentName?.toString() ?? '',
                    val.weightPercent?.toFixed(3) ?? '',
                    val.molePercent?.toFixed(3) ?? '',
                    val.targetMolePercent?.toFixed(3)?.toString() ?? '',
                    val.errorPercent?.toFixed(3)?.toString() ?? ''
                ]);

                const tableStartRow = rowNo++;
                GenerateTableWithBorders(swellingWorksheet, tableHeader, tableBody, tableStartRow, 3, borderStyle);
                swellingWorksheet.getColumn(reportConstants.d).width = 15;

                const dataStartRow = tableStartRow + tableHeader.length;
                const dataEndRow = dataStartRow + tableBody.length - 1;

                for (let r = dataStartRow; r <= dataEndRow; r++) {
                    const cCell = swellingWorksheet.getCell(`C${r}`);
                    cCell.font = { ...(cCell.font ?? {}), bold: true };

                    const dCell = swellingWorksheet.getCell(`D${r}`);
                    dCell.font = { ...(dCell.font ?? {}), bold: true };
                }

                rowNo = tableStartRow + tableHeader.length + tableBody.length;
                rowNo += 2;

                swellingWorksheet.mergeCells(reportConstants.c21, reportConstants.j22);

                swellingWorksheet.mergeCells(reportConstants.c4, reportConstants.j5);
                const textReportTitleCell = swellingWorksheet.getCell(reportConstants.c4);
                textReportTitleCell.value = wellName + ' ' + reportConstants.swellInjection;
                textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
                textReportTitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

                swellingWorksheet.mergeCells(reportConstants.c6, reportConstants.e7);
                const textReportTitleCellSmall = swellingWorksheet.getCell(reportConstants.c6);
                textReportTitleCellSmall.value = reportConstants.injectionGasComposition;
                textReportTitleCellSmall.font = { name: reportConstants.eMprintCondensed, size: 13 };
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function GenerateTableWithBorders(
            worksheet: Worksheet,
            tableHeader: string[][],
            tableBody: (string | number)[][],
            tableStartRow: number,
            tableStartColumn: number,
            borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; } }
        ) {
            tableHeader.forEach((headerRow, idx) => {
                headerRow.forEach((header, colIdx) => {
                    const cell = worksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx);
                    cell.value = header;
                    cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
                    cell.border = {
                        top: borderStyle.top,
                        bottom: borderStyle.bottom,
                        left: borderStyle.left,
                        right: borderStyle.right,
                    } as Partial<Borders>;

                    const headerValue = header.split('\n');
                    if (headerValue && headerValue.length > 0 && headerValue[0].length > 0) {
                        worksheet.getColumn(tableStartColumn + colIdx).width = headerValue[0].length + 2;
                    }
                    else {
                        worksheet.getColumn(tableStartColumn + colIdx).width = 8;
                    }
                    worksheet.getRow(tableStartRow + idx).height = 30;
                });
            });

            tableBody.forEach((row, rowIdx) => {
                row.forEach((cellValue, colIdx) => {
                    const cell = worksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
                    setNumericOrTextCellValue(cell, cellValue);
                    cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                    cell.border = {
                        bottom: borderStyle.bottom,
                        left: borderStyle.left,
                        right: borderStyle.right,
                    } as Partial<Borders>;
                });
            });
        }

        async function DisplayLogoAndWellName(report: ReportPDFSwellingRenderService, worksheet: Worksheet) {
            
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

           
            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart = reportConstants.c;
                const columnEnd = reportConstants.i;
                report._reportGeneralUtilityService.AddSubHeaderToWorksheet(worksheet, subHeader, columnStart + reportConstants.one, columnEnd + '2');
            }
        }
    }

    GenerateSwellingCalculatedCompBalData(workbook: Workbook, worksheetName: string, swellingExp: SwellingFinalReport, subHeader: string) {
        try {
            const swellingWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            swellingWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(swellingWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,// 0 automatic
                fitToHeight: 1,// 0 automatic
                scale: 70,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            DisplayLogoAndWellName(this, swellingWorksheet);

            if (swellingExp !== undefined && swellingExp !== null) {

                const additionsCount = 3;
                let rowNo = 7;

                // Insert first header row (Original Fluid + Additions)
                const originalFluidCell = swellingWorksheet.getCell(rowNo, 4);
                originalFluidCell.value = reportConstants.originalFluid;
                originalFluidCell.font = { bold: true };
                originalFluidCell.border = borderStyle;

                for (let i = 0; i < additionsCount; i++) {
                    const cell = swellingWorksheet.getCell(rowNo, 5 + i);
                    cell.value = `Addition ${i + 1}`;
                    cell.font = { bold: true };
                    cell.border = borderStyle;
                    cell.alignment = { horizontal: reportConstants.center };
                }
                rowNo++;

                // Insert second header row (Percentages)
                swellingWorksheet.getCell(rowNo, 4).value = '';
                for (let i = 0; i < additionsCount; i++) {
                    const key = `${reportConstants.addition}${i + 1}` as keyof Vw_SwellCalculatedCompositionalBalanceData;
                    const rawVal = swellingExp.swellCalculatedCompositionalBalance[0][key];
                    const cell = swellingWorksheet.getCell(rowNo, 5 + i);
                    if (rawVal !== null && rawVal !== undefined && rawVal !== '') {
                        cell.value = Number(rawVal);
                        cell.numFmt = '0.0%';
                    } else {
                        cell.value = '';
                    }
                    cell.font = { bold: true };
                    cell.border = borderStyle;
                    cell.alignment = { horizontal: reportConstants.center };
                }
                rowNo++;

                // Third header row (actual column titles)
                swellingWorksheet.mergeCells(reportConstants.b8, reportConstants.c8);
                const tableHeader = [
                    ['', reportConstants.component, reportConstants.molePercent, ...Array(additionsCount).fill(reportConstants.molePercent)]
                ];

                const items = swellingExp.swellCalculatedCompositionalBalance;

                const toMolePercent = (value: string | number | null | undefined): string | number =>
                    value === null || value === undefined || value === '' || isNaN(Number(value)) ? '' : Number(value).toFixed(3);

                // 1) Normal component rows (skip Totals for now)
                //    - skip rows where originalFluidMolePercent is null/undefined/0
                const tableBody = items
                    .filter((item: Vw_SwellCalculatedCompositionalBalanceData) =>
                        item.component !== reportConstants.totals &&
                        item.originalFluidMolePercent !== null &&
                        item.originalFluidMolePercent !== undefined &&
                        Number(item.originalFluidMolePercent) !== 0
                    )
                    .map((item: Vw_SwellCalculatedCompositionalBalanceData) => {
                        const row: (string | number)[] = [
                            item.component,
                            item.componentName,
                            toMolePercent(item.originalFluidMolePercent)
                        ];
                        for (let i = 1; i <= additionsCount; i++) {
                            row.push(toMolePercent(item[`${reportConstants.addition}${i}${reportConstants.molPct}`]));
                        }
                        return row;
                    });

                // 2) Append Totals row at the end, using sumAdd{i}MolPct
                const totals = items.find((it: Vw_SwellCalculatedCompositionalBalanceData) => it.component === reportConstants.totals);
                if (totals) {
                    const totalsRow: (string | number)[] = [
                        reportConstants.totals,
                        '',
                        // Prefer sumMolPct; fall back to originalFluidMolePercent
                        totals.sumMolPct ?? totals.originalFluidMolePercent ?? ''
                    ];
                    for (let i = 1; i <= additionsCount; i++) {
                        const sumKey = `${reportConstants.sumAdd}${i}${reportConstants.molPct}` as `sumAdd${number}MolPct`;
                        const addKey = `${reportConstants.addition}${i}${reportConstants.molPct}` as `addition${number}MolPct`; // fallback if sum is missing
                        totalsRow.push(totals[sumKey] ?? totals[addKey] ?? '');
                    }
                    tableBody.push(totalsRow);
                }


                const tableStartRow = rowNo;
                GenerateTableWithBorders(swellingWorksheet, tableHeader, tableBody, tableStartRow, 2, borderStyle);

                const firstDataRow = tableStartRow + tableHeader.length;
                const lastDataRow = firstDataRow + tableBody.length - 1;
                for (let r = firstDataRow; r <= lastDataRow; r++) {
                    swellingWorksheet.getCell(r, 2).font = { name: reportConstants.eMprintCondensed, bold: true };
                    swellingWorksheet.getCell(r, 3).font = { name: reportConstants.eMprintCondensed, bold: true };
                }

                swellingWorksheet.getRow(tableStartRow).height = 25;

                // Column widths
                 swellingWorksheet.getColumn(2).width = 11;// Component Name
                swellingWorksheet.getColumn(3).width = 25;// Component Name
                for (let i = 0; i <= additionsCount; i++) {
                    swellingWorksheet.getColumn(4 + i).width = 12;// Addition columns
                }
                // Adjust rowNo manually
                rowNo = tableStartRow + tableHeader.length + tableBody.length;

                swellingWorksheet.mergeCells(reportConstants.b4, reportConstants.i5);
                const textReportTitleCell = swellingWorksheet.getCell(reportConstants.b4);
                textReportTitleCell.value = reportConstants.calculatedCompositionalBalance;
                textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function GenerateTableWithBorders(
            worksheet: Worksheet,
            tableHeader: string[][],
            tableBody: (string | number)[][],
            tableStartRow: number,
            tableStartColumn: number,
            borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; } }
        ) {
            tableHeader.forEach((headerRow, idx) => {
                headerRow.forEach((header, colIdx) => {
                    const cell = worksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx);
                    cell.value = header;
                    cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
                    cell.border = {
                        top: idx === 0 ? { style: reportConstants.thin } : borderStyle.top,
                        bottom: idx === tableHeader.length - 1 ? { style: reportConstants.double } : borderStyle.bottom,
                        left: colIdx === 0 ? { style: reportConstants.thin } : borderStyle.left,
                        right: colIdx === headerRow.length - 1 ? { style: reportConstants.thin } : borderStyle.right,
                    } as Partial<Borders>;

                    const headerValue = header.split('\n');
                    if (headerValue && headerValue.length > 0 && headerValue[0].length > 0) {
                        worksheet.getColumn(tableStartColumn + colIdx).width = headerValue[0].length + 2;
                    }
                    else {
                        worksheet.getColumn(tableStartColumn + colIdx).width = 8;
                    }
                    worksheet.getRow(tableStartRow + idx).height = 80;
                });
            });

            tableBody.forEach((row, rowIdx) => {
                row.forEach((cellValue, colIdx) => {
                    const cell = worksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
                    setNumericOrTextCellValue(cell, cellValue);
                    cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                    cell.border = {
                        bottom: rowIdx === tableBody.length - 1 ? { style: reportConstants.thin } : borderStyle.bottom,
                        left: colIdx === 0 ? { style: reportConstants.thin } : borderStyle.left,
                        right: colIdx === row.length - 1 ? { style: reportConstants.thin } : borderStyle.right,
                    } as Partial<Borders>;
                });
            });
        }

        async function DisplayLogoAndWellName(report: ReportPDFSwellingRenderService, worksheet: Worksheet) {
            
            const exxonLogo = await report._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

        
            worksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            if (subHeader !== '') {
                const columnStart = reportConstants.c;
                const columnEnd = reportConstants.h;
                report._reportGeneralUtilityService.AddSubHeaderToWorksheet(worksheet, subHeader, columnStart + reportConstants.one, columnEnd + '2');
            }
        }
    }
}